import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import moment from "moment";
import XLSX from "xlsx";
import Datetime from "react-datetime";
import { useQuery, useSubscription, useLazyQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import IconButton from "@material-ui/core/IconButton";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import ReceiptIcon from "@material-ui/icons/Receipt";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import FiberNewIcon from "@material-ui/icons/FiberNew";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import context from "utils/context";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import Button from "designV2/components/Button";
import Field from "designV2/components/Field";
import MetricCard from "designV2/components/MetricCard";
import FilterDropdown from "designV2/components/FilterDropdown";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import DialogShell from "designV2/components/DialogShell";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import OrderDetails from "./OrderDetails";
import DeliveryStatus from "./DeliveryStatus";
import "./orders.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

// Every field below is consumed either by this table or by the two
// dialogs. OrderDetails needs user.fullName, finalPrice, createdAt,
// tracking.trackId and cart.products; DeliveryStatus additionally needs
// shop.name, trip.origin/destinations and user._id. Removing any of them
// silently breaks a dialog, so they stay.
const ORDER_FIELDS = `
  _id
  user {
    _id
    fullName
    phoneNumber
  }
  shop {
    name
  }
  shipmentModel
  finalPrice
  commission
  createdAt
  finished
  status
  trip {
    _id
    trackId
    origin {
      coordinates
    }
    destinations {
      coordinates
    }
  }
  tracking {
    trackId
  }
  cart {
    products {
      quantity
      product {
        _id
        title
      }
    }
  }
`;

const GET_ORDERS = gql`
  query getOrdersByShopAdmin(
    $limit: Int
    $skip: Int
    $_id: ID
    $user: ID
    $shipmentModel: OrderShipmentModel
    $finalPrice: Float
    $commission: Float
    $finished: Boolean
    $tracking: OrderTrackingQueryInput
    $status: OrderStatus
    $createdAt: Date
    $createdAtSort: Int
    $finalPriceSort: Int
    $passengerName: String
    $passengerPhoneNumber: String
    $createdAtFrom: Date
    $finalPriceFrom: Float
  ) {
    getOrdersByShopAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: {
        _id: $_id
        user: $user
        shipmentModel: $shipmentModel
        tracking: $tracking
        finalPrice: $finalPrice
        commission: $commission
        finished: $finished
        status: $status
        createdAt: $createdAt
        passengerName: $passengerName
        passengerPhoneNumber: $passengerPhoneNumber
        createdAtFrom: $createdAtFrom
        finalPriceFrom: $finalPriceFrom
      }
      sort: { createdAt: $createdAtSort, finalPrice: $finalPriceSort }
    ) {
      ${ORDER_FIELDS}
    }
  }
`;

const GET_ORDERS_COUNT = gql`
  query getOrdersByShopAdminCount(
    $_id: ID
    $user: ID
    $shipmentModel: OrderShipmentModel
    $finalPrice: Float
    $commission: Float
    $finished: Boolean
    $tracking: OrderTrackingQueryInput
    $status: OrderStatus
    $createdAt: Date
    $passengerName: String
    $passengerPhoneNumber: String
    $createdAtFrom: Date
    $finalPriceFrom: Float
  ) {
    getOrdersByShopAdminCount(
      filters: {
        _id: $_id
        user: $user
        shipmentModel: $shipmentModel
        tracking: $tracking
        finalPrice: $finalPrice
        commission: $commission
        finished: $finished
        status: $status
        createdAt: $createdAt
        passengerName: $passengerName
        passengerPhoneNumber: $passengerPhoneNumber
        createdAtFrom: $createdAtFrom
        finalPriceFrom: $finalPriceFrom
      }
    )
  }
`;

// Aliased counts for the queue tabs — four countDocuments in one round
// trip, so the tabs show real totals rather than a tally of the page
// that happens to be loaded.
const GET_QUEUE_COUNTS = gql`
  query getOrderQueueCounts {
    paid: getOrdersByShopAdminCount(filters: { finished: false, status: PAID })
    preparing: getOrdersByShopAdminCount(
      filters: { finished: false, status: PREPARING }
    )
    prepared: getOrdersByShopAdminCount(
      filters: { finished: false, status: PREPARED }
    )
    shipping: getOrdersByShopAdminCount(
      filters: { finished: false, status: SHIPPING }
    )
  }
`;

const GET_ORDERS_SUBSCRIPTION = gql`
  subscription createOrder {
    createOrder {
      ${ORDER_FIELDS}
    }
  }
`;

const UPDATE_ORDERS_SUBSCRIPTION = gql`
  subscription updateOrder {
    updateOrder {
      ${ORDER_FIELDS}
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const PER_PAGE_OPTIONS = [25, 50, 100, 200];
const DEFAULT_PER_PAGE = 100;
const TRACK_ID_LENGTH = 10;

const STATUS_OPTIONS = [
  "ALL",
  "PENDING",
  "PAID",
  "SHIPPING",
  "DELIVERED",
  "REJECTED",
  "DELIVERY NOT ACCEPTED",
  "FINISHED DUE TO NOT PAYING",
  "PREPARING",
  "PREPARED",
];

const SHIPMENT_MODEL_OPTIONS = ["ALL", "SHOP", "SPARK"];

// Shared with the dashboard's queue panel so a status reads the same
// wherever it appears.
const STATUS_PRESENTATION = {
  PENDING: { label: "Awaiting payment", tone: "neutral" },
  PAID: { label: "New", tone: "info" },
  PREPARING: { label: "Preparing", tone: "warning" },
  PREPARED: { label: "Ready", tone: "accent" },
  SHIPPING: { label: "Shipping", tone: "info" },
  DELIVERED: { label: "Delivered", tone: "success" },
  REJECTED: { label: "Rejected", tone: "danger" },
  DELIVERY_NOT_ACCEPTED: { label: "Delivery refused", tone: "danger" },
  FINISHED_DUE_TO_NOT_PAYING: { label: "Unpaid", tone: "neutral" },
};

const QUEUE_TABS = [
  {
    key: "paid",
    status: "PAID",
    label: "New",
    icon: <FiberNewIcon />,
    tone: "info",
  },
  {
    key: "preparing",
    status: "PREPARING",
    label: "Preparing",
    icon: <HourglassEmptyIcon />,
    tone: "warning",
  },
  {
    key: "prepared",
    status: "PREPARED",
    label: "Ready",
    icon: <CheckCircleOutlineIcon />,
    tone: "default",
  },
  {
    key: "shipping",
    status: "SHIPPING",
    label: "Shipping",
    icon: <LocalShippingOutlinedIcon />,
    tone: "info",
  },
];

const EXCEL_HEADER = [
  "track ID",
  "customer name",
  "customer phone No",
  "date",
  "price",
  "status",
  "products",
];

const baseVariables = (limit, skip) => ({
  limit,
  skip,
  createdAtSort: -1,
  status: undefined,
  createdAt: undefined,
  passengerName: undefined,
  passengerPhoneNumber: undefined,
  finalPrice: undefined,
  finalPriceSort: undefined,
  shipmentModel: undefined,
  finished: false,
  tracking: { trackId: undefined, estimatedDelivery: undefined },
  createdAtFrom: undefined,
  finalPriceFrom: undefined,
});

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

const isValidDate = (value) =>
  moment(value, "MM/DD/YYYY", true).isValid() ||
  moment(value, "MM-DD-YYYY", true).isValid() ||
  moment.isMoment(value);

const describeStatus = (status) =>
  STATUS_PRESENTATION[status] || {
    label: status ? status.replace(/_/g, " ").toLowerCase() : "unknown",
    tone: "neutral",
  };

// finalPrice and tracking can both be null on a partially created order;
// the previous export called .toFixed and .trackId on them unguarded.
const money = (value) =>
  value === null || value === undefined ? "$0.00" : `$${value.toFixed(2)}`;

const productsToText = (order) =>
  order.cart && order.cart.products
    ? order.cart.products
        .map((line) => `${line.product ? line.product.title : "?"}(${line.quantity})`)
        .join(", ")
    : "";

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const Orders = (props) => {
  const setOrdersCount = useContext(context).setOrdersCount;

  /* ---------------- paging & query variables ---------------- */

  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [pageCount, setPageCount] = useState(0);
  const [variables, setVariables] = useState(
    baseVariables(DEFAULT_PER_PAGE, 0),
  );

  const { loading, error, data, refetch } = useQuery(GET_ORDERS, {
    variables,
    fetchPolicy: "network-only",
  });

  const { data: countData, refetch: refetchCount } = useQuery(
    GET_ORDERS_COUNT,
    { variables, fetchPolicy: "network-only" },
  );

  const { data: queueData, refetch: refetchQueue } = useQuery(
    GET_QUEUE_COUNTS,
    { fetchPolicy: "network-only" },
  );

  /* ---------------- filter inputs ---------------- */

  const [trackIdFilter, setTrackIdFilter] = useState("");
  const [passengerNameFilter, setPassengerNameFilter] = useState("");
  const [passengerPhoneNumberFilter, setPassengerPhoneNumberFilter] =
    useState("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [finalPriceFromFilter, setFinalPriceFromFilter] = useState("");
  const [priceFilter, setPriceFilter] = useState("");
  const [status, setStatus] = useState(STATUS_OPTIONS[0]);
  const [shipmentModelFilter, setShipmentModelFilter] = useState(
    SHIPMENT_MODEL_OPTIONS[0],
  );

  const [trackIdFilterError, setTrackIdFilterError] = useState("");
  const [dateFromFilterError, setDateFromFilterError] = useState("");
  const [dateFilterError, setDateFilterError] = useState("");
  const [noFilterModal, setNoFilterModal] = useState(false);

  const [dateSort, setDateSort] = useState("down");
  const [priceSort, setPriceSort] = useState("");

  /* ---------------- dialogs ---------------- */

  const [modal, setModal] = useState(false);
  const [orderDetail, setOrderDetail] = useState();
  const [deliveryRequestModal, setDeliveryRequestModal] = useState(false);
  const [orderDelivery, setOrderDelivery] = useState();
  const [copiedId, setCopiedId] = useState(null);

  /* ---------------- live updates ---------------- */

  const { data: subData } = useSubscription(GET_ORDERS_SUBSCRIPTION);
  const { data: subUpdateData } = useSubscription(UPDATE_ORDERS_SUBSCRIPTION);

  // The old code read the single latest subscription payload straight into
  // the render, so a second order arriving before a refetch replaced the
  // first. These accumulate instead.
  const [liveOrders, setLiveOrders] = useState([]);
  const [removedIds, setRemovedIds] = useState([]);

  useEffect(() => {
    if (!subData || !subData.createOrder) return;
    const order = subData.createOrder;
    setLiveOrders((current) =>
      [order].concat(current.filter((item) => item._id !== order._id)),
    );
    refetchQueue();
  }, [subData, refetchQueue]);

  useEffect(() => {
    if (!subUpdateData || !subUpdateData.updateOrder) return;
    const order = subUpdateData.updateOrder;
    // Delivered and rejected orders leave the active queue.
    if (order.status === "DELIVERED" || order.status === "REJECTED") {
      setRemovedIds((current) =>
        current.indexOf(order._id) === -1 ? current.concat(order._id) : current,
      );
      setLiveOrders((current) =>
        current.filter((item) => item._id !== order._id),
      );
    } else {
      setLiveOrders((current) =>
        [order].concat(current.filter((item) => item._id !== order._id)),
      );
    }
    refetchQueue();
  }, [subUpdateData, refetchQueue]);

  /* ---------------- derived list ---------------- */

  const serverOrders = useMemo(
    () => (data && data.getOrdersByShopAdmin) || [],
    [data],
  );

  const list = useMemo(() => {
    const liveIds = liveOrders.map((order) => order._id);
    const merged = liveOrders
      .map((order) => ({ ...order, isLive: true }))
      .concat(
        serverOrders.filter((order) => liveIds.indexOf(order._id) === -1),
      );
    return merged.filter((order) => removedIds.indexOf(order._id) === -1);
  }, [serverOrders, liveOrders, removedIds]);

  const totalCount = (countData && countData.getOrdersByShopAdminCount) || 0;

  // Recomputed from the rendered list, so live arrivals move it. The old
  // effect keyed off [data, loading] and ignored subscription changes.
  const pageTotal = useMemo(
    () => list.reduce((sum, order) => sum + (order.finalPrice || 0), 0),
    [list],
  );

  useEffect(() => {
    setOrdersCount(list.length);
    // Kept for the pages that read this key directly. Writing it during
    // render, as the old code did, is a side effect in the render path.
    window.localStorage.setItem("ordersCount", list.length);
  }, [list, setOrdersCount]);

  /* ---------------- filter validation ---------------- */

  useEffect(() => {
    if (!dateFromFilter) setDateFromFilterError("");
    else setDateFromFilterError(isValidDate(dateFromFilter) ? "" : "Invalid date");
  }, [dateFromFilter]);

  useEffect(() => {
    if (!dateFilter) setDateFilterError("");
    else setDateFilterError(isValidDate(dateFilter) ? "" : "Invalid date");
  }, [dateFilter]);

  useEffect(() => {
    setTrackIdFilterError(
      trackIdFilter && trackIdFilter.length !== TRACK_ID_LENGTH
        ? `Must be ${TRACK_ID_LENGTH} characters`
        : "",
    );
  }, [trackIdFilter]);

  /* ---------------- sorting ---------------- */

  useEffect(() => {
    setVariables((current) => ({
      ...current,
      createdAtSort:
        dateSort === "up" ? 1 : dateSort === "down" ? -1 : undefined,
      finalPriceSort:
        priceSort === "up" ? 1 : priceSort === "down" ? -1 : undefined,
    }));
  }, [dateSort, priceSort]);

  const onDateSortChange = () =>
    setDateSort((current) => (current === "up" ? "down" : "up"));
  const onPriceSortChange = () =>
    setPriceSort((current) => (current === "up" ? "down" : "up"));

  /* ---------------- paging ---------------- */

  useEffect(() => {
    setVariables((current) => ({
      ...current,
      limit: perPage,
      skip: pageCount * perPage,
    }));
  }, [pageCount, perPage]);

  /* ---------------- excel export ---------------- */

  const [excelRows, setExcelRows] = useState(null);
  const [showExcelButton, setShowExcelButton] = useState("firstStatus");
  const exportRequested = useRef(false);

  const [
    getOrdersByShopAdmin,
    { data: lazyData, loading: lazyLoading },
  ] = useLazyQuery(GET_ORDERS, { fetchPolicy: "network-only" });

  useEffect(() => {
    if (!exportRequested.current || lazyLoading || !lazyData) return;
    exportRequested.current = false;
    // Rebuilt from scratch each time. The old version appended to the
    // previous export's rows, so a second export contained the first.
    setExcelRows(
      lazyData.getOrdersByShopAdmin.map((item) => [
        item.tracking && item.tracking.trackId ? item.tracking.trackId : "-",
        item.user ? item.user.fullName : "-",
        item.user ? item.user.phoneNumber : "-",
        moment(item.createdAt).format("MM-DD-YYYY"),
        item.finalPrice === null || item.finalPrice === undefined
          ? "0.00"
          : item.finalPrice.toFixed(2),
        item.status ? item.status.replace(/_/g, " ").toLowerCase() : "-",
        productsToText(item),
      ]),
    );
    setShowExcelButton("done");
  }, [lazyData, lazyLoading]);

  const onDownloadData = () => {
    if (!countData) return;
    exportRequested.current = true;
    setShowExcelButton("downloading");
    getOrdersByShopAdmin({
      variables: {
        ...variables,
        skip: 0,
        limit: Math.max(countData.getOrdersByShopAdminCount, 1),
      },
    });
  };

  const onExcelFinished = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([EXCEL_HEADER].concat(excelRows || []));
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    XLSX.writeFile(wb, "Orders.xlsx");
  };

  const resetExport = () => {
    setExcelRows(null);
    setShowExcelButton("firstStatus");
    exportRequested.current = false;
  };

  /* ---------------- filter submit ---------------- */

  const hasAnyFilter = () =>
    Boolean(
      trackIdFilter ||
        variables.tracking.trackId ||
        passengerNameFilter ||
        variables.passengerName ||
        passengerPhoneNumberFilter ||
        variables.passengerPhoneNumber ||
        dateFilter ||
        variables.createdAt ||
        priceFilter ||
        variables.finalPrice ||
        finalPriceFromFilter ||
        variables.finalPriceFrom ||
        dateFromFilter ||
        variables.createdAtFrom ||
        !(status === "ALL" && !variables.status) ||
        !(shipmentModelFilter === "ALL" && !variables.shipmentModel),
    );

  const onFilterSubmit = () => {
    if (!hasAnyFilter()) {
      setNoFilterModal(true);
      return;
    }

    resetExport();
    setPageCount(0);

    setVariables((current) => ({
      ...current,
      skip: 0,
      passengerName: passengerNameFilter || undefined,
      passengerPhoneNumber: passengerPhoneNumberFilter || undefined,
      tracking:
        trackIdFilter && trackIdFilter.length === TRACK_ID_LENGTH
          ? { trackId: trackIdFilter.trim(), estimatedDelivery: undefined }
          : { trackId: undefined, estimatedDelivery: undefined },
      createdAtFrom:
        dateFromFilter && isValidDate(dateFromFilter)
          ? moment(dateFromFilter).format("YYYY-MM-DD")
          : undefined,
      createdAt:
        dateFilter && isValidDate(dateFilter)
          ? moment(dateFilter).format("YYYY-MM-DD")
          : undefined,
      finalPriceFrom: finalPriceFromFilter
        ? parseFloat(finalPriceFromFilter)
        : undefined,
      finalPrice: priceFilter ? parseFloat(priceFilter) : undefined,
      status:
        status === "ALL" ? undefined : status.replace(/ /g, "_").toUpperCase(),
      shipmentModel:
        shipmentModelFilter === "ALL" ? undefined : shipmentModelFilter,
    }));
  };

  const onClearFilters = () => {
    resetExport();
    setPageCount(0);
    setVariables(baseVariables(perPage, 0));
    setTrackIdFilter("");
    setPassengerNameFilter("");
    setPassengerPhoneNumberFilter("");
    setDateFromFilter("");
    setDateFilter("");
    setFinalPriceFromFilter("");
    setPriceFilter("");
    setStatus("ALL");
    setShipmentModelFilter("ALL");
    setPriceSort("");
    setDateSort("down");
  };

  const handleEnter = (e) => {
    if (e.charCode === 13) onFilterSubmit();
  };

  // Clicking a queue tab is a shortcut for "status = X, everything else
  // cleared", which is what an operator actually wants from it.
  const applyQueueTab = (tab) => {
    resetExport();
    setPageCount(0);
    const nextStatus = variables.status === tab.status ? undefined : tab.status;
    setStatus(nextStatus ? tab.status : "ALL");
    setVariables((current) => ({ ...current, skip: 0, status: nextStatus }));
  };

  /* ---------------- active filter chips ---------------- */

  const activeChips = [];
  if (variables.tracking.trackId)
    activeChips.push({
      id: "trackId",
      label: `Track: ${variables.tracking.trackId}`,
    });
  if (variables.passengerName)
    activeChips.push({ id: "name", label: `Name: ${variables.passengerName}` });
  if (variables.passengerPhoneNumber)
    activeChips.push({
      id: "phone",
      label: `Phone: ${variables.passengerPhoneNumber}`,
    });
  if (variables.createdAtFrom)
    activeChips.push({ id: "dateFrom", label: `From: ${variables.createdAtFrom}` });
  if (variables.createdAt)
    activeChips.push({ id: "dateTo", label: `To: ${variables.createdAt}` });
  if (variables.finalPriceFrom !== undefined)
    activeChips.push({
      id: "priceFrom",
      label: `Min: ${money(variables.finalPriceFrom)}`,
    });
  if (variables.finalPrice !== undefined)
    activeChips.push({
      id: "priceTo",
      label: `Max: ${money(variables.finalPrice)}`,
    });
  if (variables.status)
    activeChips.push({
      id: "status",
      label: `Status: ${describeStatus(variables.status).label}`,
    });
  if (variables.shipmentModel)
    activeChips.push({
      id: "shipment",
      label: `Delivery: ${variables.shipmentModel}`,
    });

  const clearChip = (id) => {
    resetExport();
    setPageCount(0);
    const patch = { skip: 0 };
    if (id === "trackId") {
      setTrackIdFilter("");
      patch.tracking = { trackId: undefined, estimatedDelivery: undefined };
    }
    if (id === "name") {
      setPassengerNameFilter("");
      patch.passengerName = undefined;
    }
    if (id === "phone") {
      setPassengerPhoneNumberFilter("");
      patch.passengerPhoneNumber = undefined;
    }
    if (id === "dateFrom") {
      setDateFromFilter("");
      patch.createdAtFrom = undefined;
    }
    if (id === "dateTo") {
      setDateFilter("");
      patch.createdAt = undefined;
    }
    if (id === "priceFrom") {
      setFinalPriceFromFilter("");
      patch.finalPriceFrom = undefined;
    }
    if (id === "priceTo") {
      setPriceFilter("");
      patch.finalPrice = undefined;
    }
    if (id === "status") {
      setStatus("ALL");
      patch.status = undefined;
    }
    if (id === "shipment") {
      setShipmentModelFilter("ALL");
      patch.shipmentModel = undefined;
    }
    setVariables((current) => ({ ...current, ...patch }));
  };

  const onRefresh = () => {
    setLiveOrders([]);
    setRemovedIds([]);
    refetch();
    refetchCount();
    refetchQueue();
  };

  /* ---------------- render ---------------- */

  if (error) return <ErrorPage />;

  const header = (
    <PageHeader
      title="Orders"
      count={totalCount}
      description="Orders still in progress. Delivered and rejected ones move to Orders history."
      actions={
        <React.Fragment>
          <IconButton
            className="tableTopIconButton"
            size="small"
            onClick={onRefresh}
            disabled={loading}
            title="Refresh"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          <PageActionButton
            variant="export"
            icon={<CloudDownloadIcon fontSize="small" />}
            label={
              showExcelButton === "downloading"
                ? "Preparing"
                : showExcelButton === "done"
                  ? "Download Excel"
                  : "Export"
            }
            info={
              showExcelButton === "downloading"
                ? "Fetching every match"
                : showExcelButton === "done"
                  ? `${excelRows ? excelRows.length : 0} rows ready`
                  : `${totalCount} matching orders`
            }
            loading={showExcelButton === "downloading"}
            onClick={
              showExcelButton === "done" ? onExcelFinished : onDownloadData
            }
          />
        </React.Fragment>
      }
    />
  );

  if (loading && !data) {
    return (
      <div className="page ordersPage">
        <main className="main">
          {header}
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={8} columns={6} />
        </main>
      </div>
    );
  }

  return (
    <div className="page ordersPage">
      <main className="main">
        {header}

        {/* QUEUE TABS */}
        <section className="orderStatTabs">
          {QUEUE_TABS.map((tab) => (
            <button
              type="button"
              key={tab.key}
              className={`orderStatTab ${
                variables.status === tab.status ? "isActive" : ""
              }`}
              onClick={() => applyQueueTab(tab)}
              title={`Show only ${tab.label.toLowerCase()} orders`}
            >
              <MetricCard
                icon={tab.icon}
                title={tab.label}
                value={queueData ? queueData[tab.key] : 0}
                tone={
                  queueData && queueData[tab.key] > 0 ? tab.tone : "default"
                }
              />
            </button>
          ))}
        </section>

        {/* FILTERS */}
        <section className="orderFilters" onKeyPress={handleEnter}>
          <Field
            label="Track ID"
            htmlFor="orderTrackIdFilter"
            error={trackIdFilter ? trackIdFilterError : ""}
          >
            <input
              id="orderTrackIdFilter"
              type="text"
              className="dsInput"
              placeholder="10-character ID"
              value={trackIdFilter}
              onChange={(e) => setTrackIdFilter(e.target.value)}
            />
          </Field>

          <Field label="Customer name" htmlFor="orderNameFilter">
            <input
              id="orderNameFilter"
              type="text"
              className="dsInput"
              placeholder="Name..."
              value={passengerNameFilter}
              onChange={(e) => setPassengerNameFilter(e.target.value)}
            />
          </Field>

          <Field label="Customer phone" htmlFor="orderPhoneFilter">
            <input
              id="orderPhoneFilter"
              type="text"
              className="dsInput"
              placeholder="Phone..."
              value={passengerPhoneNumberFilter}
              onChange={(e) => setPassengerPhoneNumberFilter(e.target.value)}
            />
          </Field>

          <Field label="Status">
            <FilterDropdown
              label={status === "ALL" ? "All statuses" : describeStatus(
                status.replace(/ /g, "_").toUpperCase(),
              ).label}
              options={STATUS_OPTIONS.map((option) => ({
                value: option,
                label:
                  option === "ALL"
                    ? "All statuses"
                    : describeStatus(option.replace(/ /g, "_").toUpperCase())
                        .label,
              }))}
              value={status}
              active={status !== "ALL"}
              onChange={(e) => setStatus(e.target.value)}
            />
          </Field>

          <Field label="Delivery by">
            <FilterDropdown
              label={
                shipmentModelFilter === "ALL"
                  ? "Anyone"
                  : shipmentModelFilter === "SHOP"
                    ? "My shop"
                    : "Spark"
              }
              options={SHIPMENT_MODEL_OPTIONS.map((option) => ({
                value: option,
                label:
                  option === "ALL"
                    ? "Anyone"
                    : option === "SHOP"
                      ? "My shop"
                      : "Spark",
              }))}
              value={shipmentModelFilter}
              active={shipmentModelFilter !== "ALL"}
              onChange={(e) => setShipmentModelFilter(e.target.value)}
            />
          </Field>

          <Field
            label="Date range"
            error={dateFromFilterError || dateFilterError}
          >
            <div className="orderFilterPair">
              <Datetime
                disableOnClickOutside
                value={dateFromFilter}
                closeOnSelect
                onChange={(date) => setDateFromFilter(date)}
                timeFormat={false}
                inputProps={{ placeholder: "From" }}
              />
              <span className="orderFilterPairSeparator">to</span>
              <Datetime
                disableOnClickOutside
                value={dateFilter}
                closeOnSelect
                onChange={(date) => setDateFilter(date)}
                timeFormat={false}
                inputProps={{ placeholder: "To" }}
              />
            </div>
          </Field>

          <Field label="Price range">
            <div className="orderFilterPair">
              <input
                type="number"
                className="dsInput"
                placeholder="Min"
                aria-label="Minimum price"
                value={finalPriceFromFilter}
                onChange={(e) => setFinalPriceFromFilter(e.target.value)}
              />
              <span className="orderFilterPairSeparator">to</span>
              <input
                type="number"
                className="dsInput"
                placeholder="Max"
                aria-label="Maximum price"
                value={priceFilter}
                onChange={(e) => setPriceFilter(e.target.value)}
              />
            </div>
          </Field>

          <div className="orderFilterActions">
            <span className="dsFieldHint">
              Press Enter or Search to apply. Filters also drive the Excel
              export.
            </span>
            <Button onClick={onClearFilters}>Reset</Button>
            <Button variant="primary" onClick={onFilterSubmit}>
              Search
            </Button>
          </div>
        </section>

        {/* TOOLBAR */}
        <div className="tableTopBar">
          <div className="tableTopBarLeft">
            {activeChips.length > 0 ? (
              <ActiveFilterChipList
                filters={activeChips}
                onClearFilter={clearChip}
                onClearAll={onClearFilters}
              />
            ) : (
              <span className="dsFieldHint">
                Showing all active orders, newest first
              </span>
            )}
          </div>
        </div>

        {/* TABLE */}
        <div className="tableResponsive">
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell">Track ID</div>
              <div className="tableHeaderCell">Customer</div>
              <div className="tableHeaderCell">
                <button
                  type="button"
                  className={`orderSortButton ${dateSort ? "isActive" : ""}`}
                  onClick={onDateSortChange}
                  title="Sort by date"
                >
                  Date
                  {dateSort === "up" ? (
                    <ArrowUpwardIcon className="orderSortIcon" />
                  ) : dateSort === "down" ? (
                    <ArrowDownwardIcon className="orderSortIcon" />
                  ) : null}
                </button>
              </div>
              <div className="tableHeaderCell">
                <button
                  type="button"
                  className={`orderSortButton ${priceSort ? "isActive" : ""}`}
                  onClick={onPriceSortChange}
                  title="Sort by price"
                >
                  Price
                  {priceSort === "up" ? (
                    <ArrowUpwardIcon className="orderSortIcon" />
                  ) : priceSort === "down" ? (
                    <ArrowDownwardIcon className="orderSortIcon" />
                  ) : null}
                </button>
              </div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell">Products</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {list.length === 0 ? (
              <EmptyState
                icon={<ReceiptIcon />}
                title={
                  activeChips.length > 0 ? "No matches" : "No active orders"
                }
                message={
                  activeChips.length > 0
                    ? "No orders match these filters. Try widening or clearing them."
                    : "New orders will appear here the moment they come in."
                }
                action={
                  activeChips.length > 0 ? (
                    <Button onClick={onClearFilters}>Clear filters</Button>
                  ) : null
                }
              />
            ) : (
              list.map((order) => {
                const state = describeStatus(order.status);
                const trackId = order.tracking && order.tracking.trackId;
                return (
                  <div
                    className={`row ${order.isLive ? "isLive" : ""}`}
                    key={order._id}
                  >
                    <span data-label="Track ID">
                      {trackId ? (
                        <CopyToClipboard
                          text={trackId}
                          onCopy={() => setCopiedId(order._id)}
                        >
                          <span
                            className={`orderTrackId ${
                              copiedId === order._id ? "isCopied" : ""
                            }`}
                            title="Click to copy"
                          >
                            {copiedId === order._id ? "Copied" : trackId}
                          </span>
                        </CopyToClipboard>
                      ) : (
                        <span className="orderTrackIdEmpty">—</span>
                      )}
                    </span>

                    <div className="orderCustomer" data-label="Customer">
                      <span className="orderCustomerName">
                        {order.user ? order.user.fullName : "—"}
                      </span>
                      <span className="orderCustomerPhone">
                        {order.user ? order.user.phoneNumber : ""}
                      </span>
                    </div>

                    <span className="updated" data-label="Date">
                      <strong>
                        {moment(order.createdAt).format("MMM D, YYYY")}
                      </strong>
                      <span>{moment(order.createdAt).format("HH:mm")}</span>
                    </span>

                    <span className="orderPrice" data-label="Price">
                      {money(order.finalPrice)}
                    </span>

                    <div className="orderStatusCell" data-label="Status">
                      <StatusBadge status={state.tone} label={state.label} />
                      {order.shipmentModel && (
                        <span
                          className="orderShipment"
                          title={`Delivered by ${order.shipmentModel}`}
                        >
                          {order.shipmentModel}
                        </span>
                      )}
                    </div>

                    <div className="orderProducts" data-label="Products">
                      {order.cart && order.cart.products
                        ? order.cart.products.map((line) => (
                            <span
                              className="orderProductChip"
                              key={line.product ? line.product._id : Math.random()}
                              title={line.product ? line.product.title : ""}
                            >
                              <span className="orderProductQty">
                                {line.quantity}×
                              </span>
                              {line.product ? line.product.title : "?"}
                            </span>
                          ))
                        : null}
                    </div>

                    <div className="actions">
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => {
                          setOrderDetail(order);
                          setModal(true);
                        }}
                        title="Order details"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => {
                          setOrderDelivery(order);
                          setDeliveryRequestModal(true);
                        }}
                        title="Delivery status and actions"
                      >
                        <LocalShippingOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}

            {list.length > 0 && (
              <div className="orderTotalRow">
                <span className="orderTotalLabel">
                  Total on this page ({list.length}{" "}
                  {list.length === 1 ? "order" : "orders"})
                </span>
                <span className="orderTotalValue">{money(pageTotal)}</span>
              </div>
            )}
          </section>
        </div>

        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={PER_PAGE_OPTIONS}
            onPageChange={(page) => setPageCount(page)}
            onPageSizeChange={(size) => {
              setPerPage(Number(size));
              setPageCount(0);
            }}
          />
        )}
      </main>

      {/* ORDER DETAILS */}
      <DialogShell
        open={modal}
        onClose={() => setModal(false)}
        title="Order details"
        subtitle={orderDetail ? `#${orderDetail._id.slice(-8).toUpperCase()}` : ""}
      >
        {orderDetail && <OrderDetails order={orderDetail} />}
      </DialogShell>

      {/* DELIVERY STATUS */}
      <DialogShell
        open={deliveryRequestModal}
        onClose={() => setDeliveryRequestModal(false)}
        title="Delivery status"
        size="lg"
      >
        {orderDelivery && (
          <DeliveryStatus
            refetch={() => {
              refetch();
              refetchQueue();
            }}
            close={() => setDeliveryRequestModal(false)}
            order={orderDelivery}
          />
        )}
      </DialogShell>

      {/* NO FILTER */}
      <DialogShell
        open={noFilterModal}
        onClose={() => setNoFilterModal(false)}
        title="Add a filter first"
        size="sm"
        message="Fill in at least one of the filters above before searching."
        actions={[
          {
            label: "Got it",
            onClick: () => setNoFilterModal(false),
            variant: "primary",
          },
        ]}
      />
    </div>
  );
};

export default Orders;
